import type { ClinicHealth, ClinicHealthStatus, HealthState } from '@pa/api/clinic'
import { AlertTriangle, CircleCheck, CircleHelp, OctagonAlert } from 'lucide-react'
import { heartbeatText } from '../model/heartbeat'
import { STATUS_HEADLINE } from '../model/labels'

const ICON: Record<ClinicHealthStatus, typeof CircleCheck> = {
  healthy: CircleCheck,
  degraded: AlertTriangle,
  down: OctagonAlert,
  unknown: CircleHelp,
  not_connected: CircleHelp,
  inactive: CircleHelp,
}

const sessionWord = (s: HealthState) =>
  s === 'up' ? 'logged in' : s === 'down' || s === 'degraded' ? 'logged out' : 'status unknown'

/** The open incidents' titles (today's incidents that haven't ended). */
export function openIncidentTitles(health: Pick<ClinicHealth, 'components'>): string[] {
  return health.components.flatMap((c) =>
    (c.days.at(-1)?.incidents ?? []).filter((i) => i.endedAt === null).map((i) => i.title),
  )
}

/**
 * The C-12 banner (UserFlows C-12, API.md §6.16): "All systems operational", "Degraded" (with the reason), "Down"
 * (with the incident title) or "No data"; subline "Last heartbeat Ns ago · Practice Fusion logged in".
 */
export function HealthBanner({ health, now, never }: { health: ClinicHealth; now: number; never: boolean }) {
  const status = health.status
  const Icon = ICON[status]
  let headline = STATUS_HEADLINE[status]
  if (status === 'degraded' && health.degradedReason) headline = `${headline} — ${health.degradedReason}`
  if (status === 'down') {
    const titles = openIncidentTitles(health)
    if (titles.length > 0) headline = `${headline} — ${titles.join('; ')}`
  }
  const subline =
    status === 'not_connected'
      ? 'PF Keeper not connected'
      : status === 'inactive'
        ? 'This clinic is inactive'
        : `${heartbeatText(health.lastHeartbeatAt, now, never)} · Practice Fusion ${sessionWord(health.pfSession)}`
  return (
    <div className={`status-hero is-${status}`}>
      <Icon aria-hidden />
      <div>
        <b>{headline}</b>
        <span>{subline}</span>
      </div>
    </div>
  )
}
